/**
 * Màn **Nguồn & báo cáo** — báo cáo research của Luồng 1 và Luồng 3.
 *
 * **Rỗng là trạng thái ĐÚNG cho tới khi G5 chạy.** Màn phải nói ra chuyện đó, không hiện một
 * danh sách trống trông như đang tải hỏng.
 *
 * Ô số đáng nhìn nhất ở đây là **trích dẫn thiếu trường**. `Chốt` của G5 đòi mỗi trích dẫn đủ cả
 * ba: `url` · `tenNguon` · `ngayTruyCap`. Thiếu một trường thì câu khẳng định đó **không kiểm
 * chứng được** — mà nội dung này đứng tên một công ty phân phối thiết bị y tế thật, nên một câu
 * không kiểm chứng được là rủi ro thật, không phải "bài viết chưa hay".
 */

import { useQuery } from "@tanstack/react-query";

import { NHAN_TANG } from "@autoime/shared";

import { api, type BaoCaoNguon } from "../api.ts";
import { KhoiNguonTinCay } from "./NguonTinCay.tsx";
import { khoa } from "../lib/khoa.ts";
import { gioVN } from "../lib/nhan.ts";
import {
  Bao,
  Chip,
  DangTai,
  DauThe,
  DauTrang,
  Doc,
  Khoi,
  LuoiDoc,
  Rong,
  The,
} from "../thanh-phan/giao-dien.tsx";

/**
 * Nhãn tầng — **đọc từ `@autoime/shared`**, không viết tay.
 *
 * Bản viết tay cũ đứng cạnh một bản viết tay thứ hai trong khối ô nhập nguồn, trên đúng màn này.
 */
const TEN_TANG: Record<number, string> = NHAN_TANG;

export function Nguon() {
  const { data, isPending, error } = useQuery({ queryKey: khoa.nguon, queryFn: api.nguon });

  /**
   * ⚠️ Khối ô nhập nguồn phải sống SÓT qua cả hai lối ra sớm dưới đây.
   *
   * Nó là hàng rào an toàn của Luồng 3 và có query riêng; treo nó sau `if (error)` của query
   * *báo cáo* nghĩa là `/api/nguon` hỏng thì **ô nhập biến mất**, không lỗi nào nổ ra. Cùng họ
   * với `P-11`: một điều khiển tồn tại trong mã mà không có trên màn hình.
   */
  if (error) {
    return (
      <div className="min-w-0 space-y-4">
        <DauTrang tieuDe="Nguồn & báo cáo" />
        <KhoiNguonTinCay />
        <Bao muc="loi" tieuDe="Không tải được danh sách báo cáo">
          {error.message}
        </Bao>
      </div>
    );
  }
  if (isPending) {
    return (
      <div className="min-w-0 space-y-4">
        <DauTrang tieuDe="Nguồn & báo cáo" />
        <KhoiNguonTinCay />
        <DangTai />
      </div>
    );
  }

  const tongTrichDan = data.ds.reduce((t, r) => t + r.soTrichDan, 0);
  const tongThieu = data.ds.reduce((t, r) => t + r.soTrichDanThieuTruong, 0);
  const dungDuoc = data.ds.filter((r) => r.dungDuocDeVietBai).length;

  return (
    <div className="min-w-0 space-y-5">
      <DauTrang
        tieuDe="Nguồn & báo cáo"
        moTa="Báo cáo research của Luồng 1 (sự kiện hiện trường) và Luồng 3 (nghiên cứu ngoài)."
      />

      {/* Danh sách nguồn đứng TRƯỚC báo cáo: chưa có nguồn thì không bao giờ có báo cáo. */}
      <KhoiNguonTinCay />

      {data.tong === 0 ? (
        <>
          <Bao muc="thuong" tieuDe="Chưa có báo cáo nào — đây là trạng thái đúng">
            Luồng 3 (research) đang dựng — xem khối nguồn phía trên. Luồng 1 (Telegram) thuộc{" "}
            <strong>G6</strong>, chưa dựng. Luồng 2 — catalogue — đang chạy, và luồng đó{" "}
            <strong>không sinh báo cáo research</strong>.
          </Bao>
          <Rong tieuDe="Danh sách trống vì chưa có gì được tạo">
            Khác hẳn &ldquo;chưa tải được&rdquo; — API trả về 0 bản ghi, và 0 ở đây là con số đã đo.
          </Rong>
        </>
      ) : (
        <>
          <LuoiDoc cot={4}>
            <Doc nhan="báo cáo" giaTri={data.tong} />
            <Doc nhan="dùng được để viết" giaTri={dungDuoc} sacThai={dungDuoc > 0 ? "tot" : undefined} />
            <Doc nhan="trích dẫn" giaTri={tongTrichDan} />
            {/* Ô đáng nhìn nhất. Thiếu trường ⇒ câu khẳng định đó không kiểm chứng được. */}
            <Doc
              nhan="trích dẫn THIẾU trường"
              giaTri={tongThieu}
              sacThai={tongThieu > 0 ? "xau" : "tot"}
              ghiChu="cần đủ url + tên nguồn + ngày"
            />
          </LuoiDoc>

          {tongThieu > 0 && (
            <Bao muc="loi" tieuDe={`${tongThieu} trích dẫn thiếu trường bắt buộc`}>
              Chốt của G5 đòi mỗi trích dẫn đủ <strong>url · tên nguồn · ngày truy cập</strong>.
              Thiếu một trường thì câu khẳng định gắn với nó không kiểm chứng được — và nội dung
              này đứng tên một công ty phân phối thiết bị y tế thật.
            </Bao>
          )}

          {data.ds.map((r: BaoCaoNguon) => (
            <Khoi
              key={r._id}
              nhan={`${r.siteSlug} · ${gioVN(r.luc)}`}
              id={`nguon-${r._id}`}
              tomTat={
                r.soTrichDanThieuTruong > 0 ? (
                  <span className="font-medium text-loi-600">
                    {r.soTrichDanThieuTruong}/{r.soTrichDan} trích dẫn thiếu trường
                  </span>
                ) : (
                  `${r.soTrichDan} trích dẫn đủ trường`
                )
              }
            >
              <The>
                <DauThe tieuDe="Nội dung báo cáo">
                  {r.dungDuocDeVietBai ? (
                    <Chip muc="on">dùng được</Chip>
                  ) : (
                    <Chip muc="canh">chưa dùng được</Chip>
                  )}
                </DauThe>
                <p className="min-w-0 whitespace-pre-wrap break-words text-sm text-muc-700">
                  {r.noiDung}
                </p>

                {r.trichDan.length > 0 && (
                  <>
                    <p className="mt-4 text-2xs font-medium uppercase tracking-[0.1em] text-muc-500">
                      trích dẫn
                    </p>
                    <ul className="mt-1.5 min-w-0 space-y-2">
                      {r.trichDan.map((t, i) => {
                        const thieu = [
                          !t.url && "url",
                          !t.tenNguon && "tên nguồn",
                          !t.ngayTruyCap && "ngày truy cập",
                        ].filter(Boolean) as string[];
                        return (
                          <li key={i} className="min-w-0 text-2xs">
                            <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                              <Chip muc={thieu.length > 0 ? "loi" : "thuong"}>
                                tầng {t.tang} · {TEN_TANG[t.tang] ?? "?"}
                              </Chip>
                              <span className="min-w-0 truncate text-muc-800" title={t.tenNguon}>
                                {t.tenNguon || <span className="text-loi-600">(thiếu tên nguồn)</span>}
                              </span>
                              {t.ngayTruyCap && (
                                <span className="shrink-0 text-muc-500">{t.ngayTruyCap}</span>
                              )}
                            </div>
                            {t.url ? (
                              <a
                                href={t.url}
                                target="_blank"
                                rel="noreferrer noopener"
                                className="min-w-0 break-all text-canh-600 underline"
                              >
                                {t.url}
                              </a>
                            ) : (
                              <span className="text-loi-600">(thiếu url — không tra lại được)</span>
                            )}
                            {thieu.length > 0 && (
                              <p className="text-loi-600">Thiếu: {thieu.join(", ")}</p>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </>
                )}

                {r.dongGoogleSheet && (
                  <p className="mt-3 text-2xs text-muc-500">
                    Đã ghi vào Google Sheet · dòng <code>{r.dongGoogleSheet}</code>
                  </p>
                )}
              </The>
            </Khoi>
          ))}
        </>
      )}

      <p className="text-2xs text-muc-500">
        <strong>Không tìm được nguồn đạt chuẩn là một KẾT QUẢ HỢP LỆ</strong>, không phải thất bại —
        báo cáo đó về hàng chờ với ghi chú, chứ không bịa một nguồn cho đủ.
      </p>
    </div>
  );
}
